/**
 * Settings & Admin → Data Sources (Phase 2).
 *
 * The single "configure-once" home for the shared connector_instance data-source
 * registry (Phase 1B backend at /v1/data-sources). A source registered here is
 * one row that every licensed module consumes - Discovery scans it, DAM captures
 * it, DDR routes to it. Module-bifurcated views (D1) via the category column.
 */

import { useEffect, useState } from 'react';
import {
  Database,
  Warehouse,
  HardDrive,
  Activity as ActivityIcon,
  Network,
  ShieldAlert,
  Plus,
  Trash2,
  Loader2,
  X,
  AlertTriangle,
} from 'lucide-react';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/primitives';
import {
  listDataSources,
  createDataSource,
  deleteDataSource,
  ApiError,
  DATA_SOURCE_CATEGORIES,
  type DataSource,
  type DataSourceCategory,
} from '@/lib/api';

/** DPDPA segregation of duties: the DPO oversees; IT Admin configures connectors. */
const IT_ADMIN_ONLY_MSG =
  'Registering data sources is an IT Admin action - DPDPA segregation of duties (the DPO oversees compliance; IT Admin configures connectors).';

function describeError(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.status === 403) return IT_ADMIN_ONLY_MSG;
  return e instanceof Error ? e.message : fallback;
}

interface CatMeta {
  label: string;
  module: 'DSPM' | 'DAM' | 'DDR';
  icon: React.ComponentType<{ className?: string }>;
}
const CATEGORY_META: Record<DataSourceCategory, CatMeta> = {
  dspm_database: { label: 'Database', module: 'DSPM', icon: Database },
  dspm_warehouse: { label: 'Warehouse', module: 'DSPM', icon: Warehouse },
  dspm_storage: { label: 'Object storage', module: 'DSPM', icon: HardDrive },
  dam_native_audit: { label: 'Activity · native audit', module: 'DAM', icon: ActivityIcon },
  dam_cloud_stream: { label: 'Activity · cloud stream', module: 'DAM', icon: Network },
  dam_proxy: { label: 'Activity · proxy', module: 'DAM', icon: Network },
  ddr_target: { label: 'DDR enforcement target', module: 'DDR', icon: ShieldAlert },
};
const MODULE_TONE: Record<'DSPM' | 'DAM' | 'DDR', 'info' | 'success' | 'warning'> = {
  DSPM: 'info',
  DAM: 'success',
  DDR: 'warning',
};

function metaFor(category: string): CatMeta {
  return (
    CATEGORY_META[category as DataSourceCategory] ?? {
      label: category,
      module: 'DSPM',
      icon: Database,
    }
  );
}

export function AdminDataSources(): JSX.Element {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [actingOn, setActingOn] = useState<string | null>(null);

  const refresh = async (signal?: AbortSignal): Promise<void> => {
    try {
      const res = await listDataSources();
      if (signal?.aborted) return;
      setSources(res.sources);
      setError(null);
    } catch (err) {
      if (!signal?.aborted) setError(err instanceof Error ? err.message : 'Failed to load data sources');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    void refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  const remove = async (s: DataSource): Promise<void> => {
    if (!window.confirm(`Remove data source "${s.displayName}"? Modules that reference it will lose the link.`)) return;
    setActingOn(s.id);
    try {
      await deleteDataSource(s.id);
      await refresh();
    } catch (err) {
      setError(describeError(err, 'delete failed'));
    } finally {
      setActingOn(null);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Data Sources"
        subtitle="The single registry of databases & stores you operate on. Register a source once here - Discovery scans it, DAM captures its activity, DDR routes to it. No per-module re-configuration."
        badges={[
          { label: 'Configure once', variant: 'glass' },
          { label: 'Shared registry', variant: 'info' },
        ]}
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add data source
          </Button>
        }
      />

      <p className="text-[11px] text-muted-foreground -mt-2 mb-3 flex items-center gap-1.5">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0" /> {IT_ADMIN_ONLY_MSG}
      </p>

      {loading && <LoadingState caption="Loading data sources…" />}
      {error && !loading && (
        <ErrorState title="Could not load data sources" detail={error} onRetry={() => void refresh()} />
      )}

      {!loading && (
        <section>
          <div className="flex items-end justify-between mb-3">
            <h2 className="text-xs uppercase tracking-wider text-muted-foreground">
              Registered sources ({sources.length})
            </h2>
            <Button variant="glass" size="sm" onClick={() => void refresh()}>
              Refresh
            </Button>
          </div>

          {sources.length === 0 ? (
            <GlassCard className="p-8 text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                No data sources registered yet. Add one - then enable it in any module without re-entering
                connection details.
              </p>
              <Button size="sm" onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" /> Add data source
              </Button>
            </GlassCard>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-separate border-spacing-y-1">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Source</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Module</th>
                    <th className="text-left px-3 py-2">Vendor</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((s) => {
                    const m = metaFor(s.category);
                    const Icon = m.icon;
                    return (
                      <tr key={s.id} className="bg-card/40">
                        <td className="px-3 py-2 font-medium">{s.displayName}</td>
                        <td className="px-3 py-2 text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5">
                            <Icon className="h-3.5 w-3.5" /> {m.label}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant={MODULE_TONE[m.module]}>{m.module}</Badge>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{s.vendor ?? '-'}</td>
                        <td className="px-3 py-2">
                          <Badge variant={s.status === 'active' ? 'success' : 'glass'}>{s.status}</Badge>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={actingOn === s.id}
                            onClick={() => void remove(s)}
                          >
                            {actingOn === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {addOpen && (
        <AddDataSourceModal
          onClose={() => setAddOpen(false)}
          onAdded={() => {
            setAddOpen(false);
            void refresh();
          }}
        />
      )}
    </PageContainer>
  );
}

function AddDataSourceModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }): JSX.Element {
  const [form, setForm] = useState({
    category: 'dspm_database' as DataSourceCategory,
    displayName: '',
    vendor: 'postgres',
    host: '',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ready = form.displayName.trim().length > 0;

  async function onSubmit(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const config: Record<string, unknown> = {
        vendor: form.vendor.trim(),
        ...(form.host.trim() ? { host: form.host.trim() } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        addedVia: 'ui',
      };
      await createDataSource({ category: form.category, displayName: form.displayName.trim(), config });
      onAdded();
    } catch (e) {
      setErr(describeError(e, 'Could not add data source'));
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    'w-full bg-muted/30 border border-border/50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary/60';
  const lab = 'text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-4 w-4 text-primary" /> Register data source
          </DialogTitle>
          <DialogDescription>
            One registry, every module. Once added, enable it in Discovery / DAM / DDR without re-entering details.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className={lab}>Type</label>
            <select
              className={inputCls}
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as DataSourceCategory }))}
            >
              {DATA_SOURCE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {metaFor(c).module} - {metaFor(c).label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={lab}>Display name</label>
              <input
                className={inputCls}
                placeholder="ACME Prod (Postgres)"
                value={form.displayName}
                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              />
            </div>
            <div>
              <label className={lab}>Vendor</label>
              <input
                className={inputCls}
                placeholder="postgres"
                value={form.vendor}
                onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={lab}>Host (optional)</label>
              <input
                className={inputCls}
                placeholder="db.internal:5432"
                value={form.host}
                onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
              />
            </div>
            <div>
              <label className={lab}>Notes (optional)</label>
              <input
                className={inputCls}
                placeholder="Treasury BU · SEBI-scoped"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          {err && (
            <div className="flex items-start gap-2 rounded-md border border-sev-high/40 bg-sev-high/5 p-3 text-xs">
              <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Could not add data source</div>
                <div className="text-muted-foreground mt-0.5">{err}</div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" onClick={() => void onSubmit()} disabled={!ready || busy}>
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Adding…
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" /> Register
                </>
              )}
            </Button>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={onClose}>
              <X className="h-4 w-4" /> Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
